// Compress a user-picked image into a small, square, base64-encoded JPEG
// for use as a member avatar. Targets ~15–30KB which easily fits under the
// 120KB server limit (leaves headroom for base64 bloat).
//
// Steps:
//   1. Read the File into an Image via createObjectURL.
//   2. Find the smaller side; center-crop to a square.
//   3. Draw the square at 256x256 onto an offscreen canvas.
//   4. Export as JPEG at 0.82 quality → data URL.
//
// Returns a Promise<string> resolving to a `data:image/jpeg;base64,...` URL.

const TARGET_SIZE = 256;
const JPEG_QUALITY = 0.82;

export async function compressAvatarFile(file) {
  if (!file || !file.type.startsWith("image/")) {
    throw new Error("Please choose an image file.");
  }
  // Reject obvious non-starters early (>10MB raw) so iOS HEIC users get a
  // clear message rather than a hung canvas.
  if (file.size > 10 * 1024 * 1024) {
    throw new Error("Image is too big — try one under 10MB.");
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    const size = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = (img.naturalWidth - size) / 2;
    const sy = (img.naturalHeight - size) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = TARGET_SIZE;
    canvas.height = TARGET_SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas not supported");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, sx, sy, size, size, 0, 0, TARGET_SIZE, TARGET_SIZE);
    const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    if (!dataUrl || !dataUrl.startsWith("data:image/")) {
      throw new Error("Could not read this image — try a different one.");
    }
    return dataUrl;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Couldn't open this image."));
    img.src = src;
  });
}
